import { askPendoSocketRoute, fetchConversation as defaultFetcher } from './ask-pendo.helper';

import { FRAME } from './ask-pendo.frames';

/**
 * A stable identity for one user message, so a repeat can be recognised.
 *
 * Derived from the message rather than generated per send, so the *same* message
 * queued and later flushed, or re-sent by a remount, carries the same id. A fresh
 * id per send would defeat the point.
 */
function messageIdFor (message) {
    if (!message) return null;
    if (message.id) return message.id;
    if (message.messageId) return message.messageId;
    if (message.timestamp) return `ts:${message.timestamp}`;

    return null;
}

export const CONVERSATION_SOCKET_STATE = Object.freeze({
    CLOSED: 'closed',
    CONNECTING: 'connecting',
    OPEN: 'open',
    RECONNECTING: 'reconnecting',
});

const RECONNECT_BACKOFFS_MS = [1000, 2000, 4000, 8000, 16_000];
const NORMAL_CLOSE_CODES = new Set([1000, 1001]);
// Uvicorn closes every open socket with this on SIGTERM, so it is what a deploy looks
// like from here: the server is coming back, just not this instance.
const SERVICE_RESTART_CLOSE_CODE = 1012;
// A deploy closes every socket at the same instant, and each one re-fetches the
// conversation before reconnecting, so a fixed schedule brings the fleet back in
// lockstep. Applied to every attempt: retries synchronise as neatly as the first try.
const RECONNECT_JITTER_RATIO = 0.5;
// Cloud Run counts an open socket as one in-flight request and caps it at its
// --timeout, so the platform will sever this connection on a schedule whatever we do.
// Recycling first, at a margin inside that, means the cut is ours: taken between turns,
// where a reconnect costs nothing. Left to the platform it lands mid-turn often enough
// to matter, and a turn cut that way is completed and charged for upstream but never
// persisted, because the proxy only saves on `done`.
//
// Comfortably inside the 3600s ceiling rather than close to it: the margin has to cover
// a long turn running past the recycle, since a turn in flight defers it.
const RECYCLE_AFTER_MS = 45 * 60 * 1000;

export function buildConversationSocketUrl (conversationId, subscriptionId, agentRoute = askPendoSocketRoute()) {
    // No socket route for this user's agent, so there is no URL to build and the caller
    // has to fall back to the request path.
    if (!agentRoute) return null;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const { host } = window.location;

    const pageParams = new URLSearchParams(window.location.search);
    const forwardedParams = ['revision', 'claude_worker_version', 'version'];
    const query = new URLSearchParams();
    forwardedParams.forEach((param) => {
        const value = pageParams.get(param);
        if (value !== null) {
            query.set(param, value);
        }
    });

    // The agent is chosen by route on the socket exactly as it is on SSE, so the
    // deepagents flag and the socket flag stay independent of each other.
    const base = `${protocol}//${host}/api/streaming/s/${subscriptionId}/ws/ask-pendo/${agentRoute}`;

    return query.toString() ? `${base}?${query.toString()}` : base;
}

export class ConversationSocket {
    constructor ({
        conversationId,
        url,
        onEvent,
        lens = null,
        fetcher = defaultFetcher,
        webSocketCtor = typeof WebSocket === 'undefined' ? null : WebSocket,
        reconnectBackoffsMs = RECONNECT_BACKOFFS_MS,
        recycleAfterMs = RECYCLE_AFTER_MS,
    } = {}) {
        if (!conversationId) throw new Error('ConversationSocket requires a conversationId');
        if (!url) throw new Error('ConversationSocket requires a url');
        if (typeof onEvent !== 'function') throw new Error('ConversationSocket requires onEvent callback');
        if (!webSocketCtor) throw new Error('ConversationSocket requires a WebSocket implementation');

        this.conversationId = conversationId;
        this.url = url;
        this.onEvent = onEvent;
        // Read on every open rather than captured once: a reconnect has to send what
        // the Lens is now, and the first completed turn can have locked it since.
        this.lens = lens;
        this.fetcher = fetcher;
        this.WebSocketCtor = webSocketCtor;
        this.reconnectBackoffsMs = reconnectBackoffsMs;

        this.state = CONVERSATION_SOCKET_STATE.CLOSED;
        this.ws = null;
        this.conversation = null;
        this.reconnectAttempts = 0;
        this.reconnectTimer = null;
        this.intentionallyClosed = false;
        this.pendingUserMessages = [];
        this.recycleAfterMs = recycleAfterMs;
        this.recycleTimer = null;
        // A recycle waits for the turn to finish, so the socket is never swapped out
        // from under one. Tracked here rather than read from the store because the
        // socket already sees every frame that starts and ends a turn.
        this.turnInFlight = false;
        this.recyclePending = false;
    }

    /**
     * Hand this live socket to a new owner, rather than closing it and opening
     * another.
     *
     * The panel remounts as the user moves, and each mount wants its own
     * callbacks — but replacing the socket at that moment destroys a turn that
     * is mid-flight, and the answer the user was already watching goes with it.
     *
     * Everything replaced here is a callback into the current owner. The
     * connection, the queue and the conversation are deliberately left alone:
     * they belong to the exchange, not the owner.
     */
    adopt ({ onEvent } = {}) {
        if (typeof onEvent === 'function') this.onEvent = onEvent;
    }

    get isLive () {
        return !this.intentionallyClosed && this.state !== CONVERSATION_SOCKET_STATE.CLOSED;
    }

    open (conversation) {
        if (this.state !== CONVERSATION_SOCKET_STATE.CLOSED) return;
        if (!conversation) throw new Error('open requires a conversation');

        this.conversation = conversation;
        this.intentionallyClosed = false;
        this.reconnectAttempts = 0;
        this._connect();
    }

    /**
     * Send what the user typed, or hold it until there is a socket to send it on.
     *
     * Queued rather than dropped: the user has already seen their message appear in
     * the panel, so losing it silently is worse than sending it a second late.
     */
    sendUserMessage (message) {
        if (this.intentionallyClosed) return;

        if (this.state === CONVERSATION_SOCKET_STATE.OPEN) {
            this.turnInFlight = true;
            this._sendFrame({
                type: FRAME.USER_MESSAGE,
                [FRAME.MESSAGE_ID]: messageIdFor(message),
                message,
            });

            return;
        }

        this.pendingUserMessages.push(message);

        if (this.state === CONVERSATION_SOCKET_STATE.CLOSED && this.conversation) {
            this.intentionallyClosed = false;
            this.reconnectAttempts = 0;
            this._connect();
        }
    }

    close () {
        this.intentionallyClosed = true;
        this._clearReconnectTimer();
        this._clearRecycleTimer();
        this.recyclePending = false;
        this.turnInFlight = false;
        this.pendingUserMessages = [];

        if (this.ws) {
            try {
                this.ws.close(1000, 'intentional');
            } catch {
                // no-op
            }
            this.ws = null;
        }
        this._setState(CONVERSATION_SOCKET_STATE.CLOSED);
    }

    _connect () {
        this._setState(CONVERSATION_SOCKET_STATE.CONNECTING);
        try {
            this.ws = new this.WebSocketCtor(this.url);
        } catch {
            this.ws = null;
            this._scheduleReconnect();

            return;
        }
        this.ws.addEventListener('open', this._handleOpen);
        this.ws.addEventListener('message', this._handleMessage);
        this.ws.addEventListener('close', this._handleClose);
        this.ws.addEventListener('error', this._handleError);
    }

    _handleOpen = () => {
        this.reconnectAttempts = 0;
        this._setState(CONVERSATION_SOCKET_STATE.OPEN);
        // The server resolves this against the caller's entitlements and ignores it
        // when the conversation has locked its Lens, so it is a request rather than
        // an instruction. Omitted when there is none, which reads as All data.
        const lens = typeof this.lens === 'function' ? this.lens() : null;
        this._sendFrame({
            type: FRAME.CONVERSATION_INIT,
            conversation: this.conversation,
            ...(lens ? { lens } : {}),
        });

        const queued = this.pendingUserMessages;
        this.pendingUserMessages = [];
        for (const message of queued) {
            this.turnInFlight = true;
            this._sendFrame({
                type: FRAME.USER_MESSAGE,
                [FRAME.MESSAGE_ID]: messageIdFor(message),
                message,
            });
        }

        this._armRecycle();
    };

    _handleMessage = (event) => {
        let frame;
        try {
            frame = JSON.parse(event.data);
        } catch {
            return;
        }
        if (!frame || typeof frame !== 'object') return;

        // Wire contract: the server pings to keep the connection alive; we reply with a pong.
        if (frame.type === FRAME.PING) {
            this._sendFrame({ type: FRAME.PONG });

            return;
        }

        if (frame.type === 'done') {
            this.turnInFlight = false;
        }

        // Handed to the owner before any recycle, so the panel has committed the turn
        // before the connection under it is replaced.
        this.onEvent(frame);

        if (frame.type === 'done' && this.recyclePending) {
            this._recycle();
        }
    };

    _handleClose = (event) => {
        this.ws = null;
        this._clearRecycleTimer();
        this.recyclePending = false;

        if (this.intentionallyClosed) {
            this._setState(CONVERSATION_SOCKET_STATE.CLOSED);

            return;
        }

        if (event && NORMAL_CLOSE_CODES.has(event.code)) {
            this._setState(CONVERSATION_SOCKET_STATE.CLOSED);

            return;
        }

        // A deploy is the one disconnect we know the cause of, and it is not the user's
        // connection being unwell — another instance is already serving. Waiting out a
        // backoff would just leave them looking at a dead panel. Reconnect straight away,
        // and if that instance is going too the normal backoff takes over from here.
        if (event && event.code === SERVICE_RESTART_CLOSE_CODE) {
            this._scheduleReconnect({ immediate: true });

            return;
        }

        this._scheduleReconnect();
    };

    _handleError = () => {
        // close handler runs after error; reconnect lives there.
    };

    _sendFrame (frame) {
        if (this.ws && this.ws.readyState === 1) {
            const payload = JSON.stringify(frame);
            // Kept at debug so it is hidden unless someone opts into Verbose: while the
            // flag is being widened, being able to read the frames in a real session is
            // the difference between diagnosing a report and reproducing one.
            // eslint-disable-next-line no-console
            console.debug('[askPendo ws] →', frame.type, payload.length > 500 ? `${payload.slice(0, 500)}…` : payload);
            this.ws.send(payload);
        }
    }

    _scheduleReconnect ({ immediate = false } = {}) {
        this._setState(CONVERSATION_SOCKET_STATE.RECONNECTING);
        const index = Math.min(this.reconnectAttempts, this.reconnectBackoffsMs.length - 1);
        const delay = immediate ? 0 : this._jittered(this.reconnectBackoffsMs[index]);
        this.reconnectAttempts += 1;

        this._clearReconnectTimer();
        this.reconnectTimer = setTimeout(() => this._reconnect(), delay);
    }

    /**
     * Spread a backoff over a window rather than firing on the dot.
     *
     * Keeps the full delay as the ceiling instead of adding to it, so retries never get
     * slower than the schedule says.
     */
    _jittered (delay) {
        return Math.round(delay * (1 - (Math.random() * RECONNECT_JITTER_RATIO)));
    }

    /**
     * Refetch before reconnecting, not after.
     *
     * The server writes the conversation once per turn, so after a drop its copy is
     * usually ahead of ours. Reconnecting first would send our stale history as the
     * init frame and make this tab briefly authoritative over a turn it missed.
     */
    async _reconnect () {
        if (this.intentionallyClosed) return;

        try {
            const response = await this.fetcher(this.conversationId);
            const freshConversation = response?.data ?? response;
            if (freshConversation) {
                this.conversation = freshConversation;
                this.onEvent({ type: FRAME.CONVERSATION_REFRESHED, conversation: freshConversation });
            }
        } catch {
            this._scheduleReconnect();

            return;
        }

        if (this.intentionallyClosed) return;
        this._connect();
    }

    _clearReconnectTimer () {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    }

    /**
     * Start the clock on replacing this connection before the platform does.
     *
     * Armed on every open, so a recycled or reconnected socket gets its own full
     * window rather than inheriting the last one's.
     */
    _armRecycle () {
        this._clearRecycleTimer();
        if (!this.recycleAfterMs) return;

        this.recycleTimer = setTimeout(() => {
            // Mid-turn the swap waits: replacing the connection now would drop the
            // stream the user is reading, which is the thing this exists to avoid.
            // The done frame takes it instead.
            if (this.turnInFlight) {
                this.recyclePending = true;

                return;
            }
            this._recycle();
        }, this.recycleAfterMs);
    }

    _clearRecycleTimer () {
        clearTimeout(this.recycleTimer);
        this.recycleTimer = null;
    }

    /**
     * Replace the connection, keeping the conversation.
     *
     * Deliberately routed through the reconnect path rather than a bare close-and-open:
     * that refetches first, so the new socket's init frame carries the server's copy
     * rather than ours, exactly as it would after an unplanned drop. The only difference
     * is that we chose the moment.
     */
    _recycle () {
        if (this.intentionallyClosed) return;

        this.recyclePending = false;
        this._clearRecycleTimer();

        if (this.ws) {
            // 1000: this is us, not a fault. Detach the handlers first so the close we
            // are about to cause does not look like a drop and schedule a backoff on
            // top of the reconnect we are already driving.
            const { ws } = this;
            this.ws = null;
            ws.removeEventListener('open', this._handleOpen);
            ws.removeEventListener('message', this._handleMessage);
            ws.removeEventListener('close', this._handleClose);
            ws.removeEventListener('error', this._handleError);
            try {
                ws.close(1000, 'recycle');
            } catch {
                // no-op
            }
        }

        this.reconnectAttempts = 0;
        this._setState(CONVERSATION_SOCKET_STATE.RECONNECTING);
        this._reconnect();
    }

    _setState (next) {
        if (this.state === next) return;
        const previous = this.state;
        this.state = next;
        this.onEvent({ type: FRAME.SOCKET_STATE, state: next, previous });
    }
}
